/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { ITrickPlayConfig } from "../interfaces";
import type { ITrickPlayEligibility } from "../interfaces";

// ── Skip Step Values ─────────────────────────────────────────────────────────

/** Forward skip step in seconds for I-Frame and Emulated modes. */
export const SEEK_FORWARD_STEP: number = 30;

/** Backward skip step in seconds for I-Frame and Emulated modes. */
export const SEEK_BACKWARD_STEP: number = 8;

// ── Timing ───────────────────────────────────────────────────────────────────

/** Interval (ms) between ticks for continuous seek in Thumbnail/Emulated modes. */
export const TICK_INTERVAL_MS: number = 1000;

/** Threshold (ms) to distinguish short press from long press. */
export const LONG_PRESS_THRESHOLD_MS: number = 400;

/**
 * Thresholds (ms) for speed level escalation during hold.
 * Index 0 = time before escalating to L2, index 1 = time before L3.
 */
export const SPEED_ESCALATION_MS: number[] = [2000, 4000];

// ── Live / Boundary ──────────────────────────────────────────────────────────

/** Default live-edge threshold in seconds. */
export const LIVE_THRESHOLD_SECONDS: number = 60;

/**
 * Seconds from seekable range boundary at which trick-play auto-stops.
 * Matches ShakaVideoPlayerImpl.TRICK_PLAY_BOUNDARY_THRESHOLD.
 */
export const TRICK_PLAY_BOUNDARY_THRESHOLD: number = 1.0;

/**
 * Smallest live seekable window (seconds) treated as a timeshift buffer.
 * Every live manifest exposes a short window of a few segments behind the
 * live edge; anything at or below this is that window, not timeshift.
 * Matches LIVE_THRESHOLD_SECONDS, the distance Go LIVE already treats as
 * "at live".
 */
export const MIN_TIMESHIFT_WINDOW_SECONDS: number = LIVE_THRESHOLD_SECONDS;

// ── Default Config ───────────────────────────────────────────────────────────

/**
 * Default TrickPlayConfig for linear (live) playback.
 * Used as the initial configuration before MSO-specific overrides are applied.
 */
export const DEFAULT_TRICKPLAY_CONFIG: Readonly<ITrickPlayConfig> = {
  enableThumbnailSeek: true,
  hasTrickPlayTrack: false,
  forwardStep: SEEK_FORWARD_STEP,
  backwardStep: SEEK_BACKWARD_STEP,
  tickIntervalMs: TICK_INTERVAL_MS,
  liveThresholdSeconds: LIVE_THRESHOLD_SECONDS,
  boundaryThreshold: TRICK_PLAY_BOUNDARY_THRESHOLD
};

// ── Restriction API ──────────────────────────────────────────────────────────

/** Default timeout (ms) for trick-play restriction API calls. */
export const RESTRICTION_API_TIMEOUT_MS: number = 5000;

// ── Default Eligibility ──────────────────────────────────────────────────────

/**
 * Default eligibility: all actions allowed.
 * Use as a starting point; override per platform/channel rules.
 */
export const DEFAULT_ELIGIBILITY: Readonly<ITrickPlayEligibility> = {
  trickPlayAllowed: true,
  startOverAvailable: true,
  restartAvailable: false,
  goLiveAvailable: true,
  allowForward: true,
  allowBackward: true,
  allowPause: true,
  seekRangeMode: "full"
};

/**
 * Fully-disabled eligibility: every trick-play action blocked and the seek
 * bar rendered non-interactive (`seekRangeMode: "none"`). Applied while the
 * network is disconnected so all trick-play controls and the progress bar are
 * disabled; the real eligibility is restored from the live session on
 * reconnect.
 */
export const DISABLED_ELIGIBILITY: Readonly<ITrickPlayEligibility> = {
  trickPlayAllowed: false,
  startOverAvailable: false,
  restartAvailable: false,
  goLiveAvailable: false,
  allowForward: false,
  allowBackward: false,
  allowPause: false,
  seekRangeMode: "none"
};
